import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, MessageCircle, Image, User, Shield, Crown, RefreshCw, UserPlus, Star, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { OnlineIndicator } from '../components/OnlineIndicator';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { DiscoverySidebar } from '../components/DiscoverySidebar';
import { Dialog, DialogContent } from '../components/ui/dialog';
import PhotoComments from '../components/PhotoComments';
import LazyImage from '../components/LazyImage';

const SCROLL_STORAGE_KEY = 'feedScrollPosition';

export default function FeedPage() {
  const { user, api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [feedBlocked, setFeedBlocked] = useState(false);
  const [feedLimit, setFeedLimit] = useState(null);
  const scrollRestoredRef = useRef(false);
  const loadMoreRef = useRef(null);
  const ITEMS_PER_PAGE = 10;

  // Continuously save scroll position (only positive values to prevent overwrites)
  useEffect(() => {
    let lastSavedScroll = 0;
    
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      // Only save if scroll is positive and different from last saved
      if (currentScroll > 0 && currentScroll !== lastSavedScroll) {
        lastSavedScroll = currentScroll;
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(currentScroll));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also save on visibility change (when user switches tabs/navigates away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && window.scrollY > 0) {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    fetchFeed();
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMoreActivities();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMore, loading, loadingMore, skip]);

  const fetchFeed = async () => {
    setLoading(true);
    setActivities([]); // Clear existing activities on refresh
    setSkip(0);
    setHasMore(true);
    setFeedBlocked(false);
    try {
      const response = await api.get(`/feed?skip=0&limit=${ITEMS_PER_PAGE}`);
      const data = response.data;
      setActivities(data.activities || []);
      setHasMore(data.has_more);
      setSkip(ITEMS_PER_PAGE);
      setFeedLimit(data.feed_limit || null);
      if (data.feed_blocked) {
        setFeedBlocked(true);
        setHasMore(false);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        // User pending or not authorized
        const message = error.response?.data?.detail;
        if (message) toast.error(message);
      } else {
        toast.error('Erreur lors du chargement du fil');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreActivities = async () => {
    if (loadingMore || !hasMore || feedBlocked) return;
    
    setLoadingMore(true);
    try {
      const response = await api.get(`/feed?skip=${skip}&limit=${ITEMS_PER_PAGE}`);
      const data = response.data;
      setActivities(prev => [...prev, ...(data.activities || [])]);
      setHasMore(data.has_more);
      setSkip(prev => prev + ITEMS_PER_PAGE);
      if (data.feed_blocked) {
        setFeedBlocked(true);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more activities:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Restore scroll position after content is loaded
  useEffect(() => {
    if (!loading && activities.length > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      const savedPosition = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      if (savedPosition) {
        const scrollY = parseInt(savedPosition, 10);
        // Use double RAF to ensure DOM is fully painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
          });
        });
      }
    }
  }, [loading, activities]);

  // Check if user is pending validation (profile_level === 'pending')
  const isPending = user.profile_level === 'pending' || (!user.is_validated && !user.is_subscribed && !user.is_admin);
  
  if (isPending) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="feed-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              En attente de validation
            </h1>
            <p className="text-gray-400 mb-6">
              Votre profil doit être validé par un administrateur ou par 5 ambassadeurs avant de pouvoir accéder au fil d'actualité.
            </p>
            <div className="bg-dark-subtle border border-white/10 rounded-sm p-4 mb-6">
              <p className="text-sm text-gray-300">
                <span className="text-gold font-semibold">{user.validation_votes || 0}/5</span> validations obtenues
              </p>
              <div className="w-full h-2 bg-dark mt-2 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-gold to-gold-dark transition-all duration-500"
                  style={{ width: `${((user.validation_votes || 0) / 5) * 100}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Assurez-vous que votre profil est complet et que vos photos respectent nos recommandations.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="feed-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex gap-8">
          {/* Main Feed Column */}
          <div className="flex-1 max-w-2xl mx-auto lg:mx-0">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <h1 className="font-serif text-2xl md:text-3xl gold-gradient-text">Fil d'actualité</h1>
              <Button 
                onClick={fetchFeed} 
                className="btn-outline-gold flex items-center gap-2 text-sm"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
            </div>

            {!user.is_subscribed && (
              <div className="glass-card p-4 md:p-6 mb-6 md:mb-8 text-center">
                <Crown className="w-8 h-8 md:w-10 md:h-10 text-gold mx-auto mb-3 md:mb-4" />
                <h3 className="font-serif text-lg md:text-xl text-white mb-2">Abonnez-vous</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Accédez à toutes les fonctionnalités : photos, messagerie, chat en direct...
                </p>
                <Link to="/subscription" className="btn-gold inline-block" data-testid="upgrade-btn">
                  Découvrir les offres
                </Link>
              </div>
            )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : activities.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-gray-400">Aucune activité pour le moment</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="activity-list">
            {activities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} isSubscribed={user.is_subscribed} api={api} />
            ))}
            
            {/* Feed blocked for validated (non-subscribed) users */}
            {feedBlocked && (
              <div className="glass-card p-8 text-center my-6">
                <Lock className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-2">Vous avez atteint la limite</h3>
                <p className="text-gray-400 mb-4">
                  Vous avez consulté {feedLimit} publications. Abonnez-vous pour voir tout le contenu !
                </p>
                <Link to="/subscription" className="btn-gold inline-flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Voir les abonnements
                </Link>
              </div>
            )}
            
            {/* Load more trigger */}
            <div ref={loadMoreRef} className="py-4">
              {loadingMore && (
                <div className="flex justify-center">
                  <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                </div>
              )}
              {!hasMore && activities.length > 0 && !feedBlocked && (
                <p className="text-center text-gray-500 text-sm">Vous avez tout vu !</p>
              )}
            </div>
          </div>
        )}
          </div>

          {/* Right Sidebar - Discovery (sticky) */}
          <div className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <DiscoverySidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ActivityCard = ({ activity, isSubscribed, api }) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [ownerPhotos, setOwnerPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  // Like a photo
  const likePhoto = async (photoId, e) => {
    if (e) e.stopPropagation();
    if (!photoId || isLiking) return;
    
    setIsLiking(true);
    try {
      await api.post(`/photos/${photoId}/like`);
      // Update likes count in ownerPhotos
      setOwnerPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, likes: (p.likes || 0) + 1 } : p
      ));
      toast.success('Photo likée !');
    } catch (error) {
      console.error('Error liking photo:', error);
      toast.error('Erreur lors du like');
    } finally {
      setIsLiking(false);
    }
  };

  // Fetch owner's photos when modal opens
  const openPhotoModal = async () => {
    setShowPhotoModal(true);
    setLoadingPhotos(true);
    
    try {
      const ownerId = activity.photo_owner_id || activity.user_id;
      const response = await api.get(`/profile/${ownerId}`);
      const photos = response.data.photos || [];
      setOwnerPhotos(photos);
      
      // Find the current photo index by matching URL or ID
      let index = photos.findIndex(p => p.id === activity.photo_id);
      
      // If not found by ID, try matching by URL
      if (index < 0 && activity.photo_url) {
        index = photos.findIndex(p => p.url === activity.photo_url);
      }
      
      // If still not found, check if the activity photo URL matches part of any photo URL
      if (index < 0 && activity.photo_url) {
        const photoFileName = activity.photo_url.split('/').pop();
        index = photos.findIndex(p => p.url && p.url.includes(photoFileName));
      }
      
      setCurrentPhotoIndex(index >= 0 ? index : 0);
      
      // If photo not in profile photos, add it to the list
      if (index < 0 && activity.photo_url) {
        const activityPhoto = { id: activity.photo_id, url: activity.photo_url };
        setOwnerPhotos([activityPhoto, ...photos]);
        setCurrentPhotoIndex(0);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      // If we can't fetch, just show the single photo
      setOwnerPhotos([{ id: activity.photo_id, url: activity.photo_url }]);
      setCurrentPhotoIndex(0);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const goToPreviousPhoto = (e) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    } else {
      setCurrentPhotoIndex(ownerPhotos.length - 1);
    }
  };

  const goToNextPhoto = (e) => {
    e.stopPropagation();
    if (currentPhotoIndex < ownerPhotos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    } else {
      setCurrentPhotoIndex(0);
    }
  };

  const currentPhoto = ownerPhotos[currentPhotoIndex] || { url: activity.photo_url, id: activity.photo_id };
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'photo_upload':
        return <Image className="w-5 h-5 text-gold" />;
      case 'photo_like':
        return <Heart className="w-5 h-5 text-red-400 fill-red-400" />;
      case 'photo_comment':
        return <MessageCircle className="w-5 h-5 text-blue-400" />;
      case 'profile_update':
        return <User className="w-5 h-5 text-gold" />;
      case 'new_member':
        return <UserPlus className="w-5 h-5 text-green-400" />;
      default:
        return <Star className="w-5 h-5 text-gold" />;
    }
  };

  const getActivityStyle = (type) => {
    switch (type) {
      case 'new_member':
        return 'border-gold/30';
      case 'photo_upload':
        return 'border-gold/30';
      case 'photo_like':
        return 'border-red-400/20 bg-red-400/5';
      case 'photo_comment':
        return 'border-blue-400/20 bg-blue-400/5';
      default:
        return '';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes}min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days === 1) return "Hier";
    return `Il y a ${days}j`;
  };

  // Get the pseudo to display (from content or from activity.pseudo)
  const getPseudo = () => {
    if (activity.pseudo) return activity.pseudo;
    // Try to extract from content
    const match = activity.content?.match(/^([^\s]+)/);
    return match ? match[1] : '?';
  };

  return (
    <div className={`glass-card p-6 hover:border-gold/30 transition-colors overflow-hidden ${getActivityStyle(activity.type)}`} data-testid="activity-card">
      <div className="flex items-start gap-4">
        <Link to={`/member/${activity.user_id}`} className="flex-shrink-0">
          <ProfileAvatar
            profileType={activity.profile_type}
            src={activity.profile_picture 
              ? getImageUrl(activity.profile_picture)
              : activity.photo_url 
                ? getImageUrl(activity.photo_url)
                : null
            }
            fallback={getPseudo()[0]?.toUpperCase() || '?'}
            size="lg"
            showOnline={true}
            isOnline={activity.is_online}
          />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-dark-subtle flex items-center justify-center">
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1">
              <p className="text-white">
                <Link 
                  to={`/member/${activity.user_id}`} 
                  className="font-semibold text-gold hover:text-gold-light transition-colors inline-flex items-center gap-1.5"
                >
                  {getPseudo()}
                  <OnlineIndicator isOnline={activity.is_online} size="sm" />
                </Link>
                {activity.type === 'new_member' && (
                  <span className="text-gray-300"> vient de rejoindre la communauté !</span>
                )}
                {activity.type === 'photo_upload' && (
                  <span className="text-gray-300"> a ajouté une nouvelle photo</span>
                )}
                {activity.type === 'photo_like' && (
                  <span className="text-gray-300">
                    {' '}a aimé une photo de{' '}
                    <Link 
                      to={`/member/${activity.photo_owner_id}`}
                      className="font-semibold text-gold hover:text-gold-light inline-flex items-center gap-1.5"
                    >
                      {activity.photo_owner_pseudo || 'un membre'}
                      <OnlineIndicator isOnline={activity.photo_owner_is_online} size="sm" />
                    </Link>
                  </span>
                )}
                {activity.type === 'photo_comment' && (
                  <span className="text-gray-300">
                    {' '}a commenté une photo de{' '}
                    <Link 
                      to={`/member/${activity.photo_owner_id}`}
                      className="font-semibold text-gold hover:text-gold-light inline-flex items-center gap-1.5"
                    >
                      {activity.photo_owner_pseudo || 'un membre'}
                      <OnlineIndicator isOnline={activity.photo_owner_is_online} size="sm" />
                    </Link>
                  </span>
                )}
                {activity.type === 'profile_update' && (
                  <span className="text-gray-300"> a mis à jour son profil</span>
                )}
              </p>
              <span className="text-xs text-gray-500">{formatTime(activity.created_at)}</span>
            </div>
          </div>

          {/* Comment preview for photo_comment activities */}
          {activity.type === 'photo_comment' && activity.comment_preview && (
            <div className="mt-3 ml-12 pl-4 border-l-2 border-blue-400/30">
              <p className="text-sm text-gray-400 italic">"{activity.comment_preview}"</p>
            </div>
          )}

          {/* Photo display for uploads, likes AND comments */}
          {activity.photo_url && (activity.type === 'photo_upload' || activity.type === 'photo_like' || activity.type === 'photo_comment') && (
            <>
              {isSubscribed ? (
                <div className="mt-4 flex justify-center">
                  <div 
                    onClick={openPhotoModal}
                    className="block cursor-pointer group max-w-sm"
                    data-testid={`photo-link-${activity.id}`}
                  >
                    <img 
                      src={getImageUrl(activity.photo_url)}
                      alt="Photo"
                      className="w-full rounded-2xl transition-transform group-hover:scale-[1.01] group-hover:brightness-110"
                    />
                  </div>
                  
                  {/* Photo Modal with Navigation and Comments */}
                  <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
                    <DialogContent className="bg-dark-paper border-white/10 max-w-5xl max-h-[90vh] overflow-hidden p-0">
                      <div className="flex flex-col md:flex-row h-full">
                        {/* Photo with Navigation */}
                        <div className="relative flex-1 flex items-center justify-center bg-black min-h-[300px] md:min-h-[500px]">
                          {/* Loading indicator */}
                          {loadingPhotos && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
                              <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                            </div>
                          )}
                          
                          {/* Previous button */}
                          {ownerPhotos.length > 1 && (
                            <button
                              onClick={goToPreviousPhoto}
                              className="absolute left-2 md:left-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                              data-testid="feed-prev-photo-btn"
                            >
                              <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                            </button>
                          )}
                          
                          {/* Photo */}
                          <img
                            src={getImageUrl(currentPhoto.url)}
                            alt="Photo"
                            className="max-w-full max-h-[50vh] md:max-h-[80vh] object-contain"
                          />
                          
                          {/* Next button */}
                          {ownerPhotos.length > 1 && (
                            <button
                              onClick={goToNextPhoto}
                              className="absolute right-2 md:right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                              data-testid="feed-next-photo-btn"
                            >
                              <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                            </button>
                          )}
                          
                          {/* Photo counter */}
                          {ownerPhotos.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                              {currentPhotoIndex + 1} / {ownerPhotos.length}
                            </div>
                          )}
                        </div>
                        
                        {/* Comments section */}
                        <div className="w-full md:w-80 lg:w-96 flex flex-col p-4 md:p-6 border-t md:border-t-0 md:border-l border-white/10 max-h-[40vh] md:max-h-none overflow-y-auto">
                          {/* Photo owner info */}
                          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                            <Link 
                              to={`/member/${activity.photo_owner_id || activity.user_id}`}
                              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                              onClick={() => setShowPhotoModal(false)}
                            >
                              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-semibold">
                                {(activity.photo_owner_pseudo || activity.pseudo || '?')[0]?.toUpperCase()}
                              </div>
                              <div>
                                <span className="text-white font-medium">
                                  {activity.photo_owner_pseudo || activity.pseudo}
                                </span>
                                <p className="text-xs text-gray-500">Voir le profil</p>
                              </div>
                            </Link>
                          </div>
                          
                          {/* Like section */}
                          <div className="py-3 border-b border-white/10 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-gray-400">
                              <Heart className="w-5 h-5 text-red-400 fill-red-400" />
                              <span>{currentPhoto.likes || 0} likes</span>
                            </div>
                            <Button
                              onClick={(e) => likePhoto(currentPhoto.id, e)}
                              disabled={isLiking}
                              className="btn-outline-gold flex items-center gap-2 text-sm px-3 py-1.5"
                              data-testid="like-photo-btn"
                            >
                              <Heart className={`w-4 h-4 ${isLiking ? 'animate-pulse' : ''}`} />
                              J'aime
                            </Button>
                          </div>
                          
                          {/* Comments - use current photo ID */}
                          <PhotoComments 
                            key={currentPhoto.id}
                            photoId={currentPhoto.id} 
                            photoOwnerId={activity.photo_owner_id || activity.user_id}
                          />
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
                <div className="mt-4 relative">
                  <div className="w-full h-48 bg-dark-subtle rounded-sm flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-dark to-transparent z-10" />
                    <div className="blur-xl opacity-30 absolute inset-0">
                      <img 
                        src={getImageUrl(activity.photo_url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="relative z-20 text-center p-4">
                      <Crown className="w-8 h-8 text-gold mx-auto mb-2" />
                      <p className="text-gray-300 text-sm mb-3">Accédez à toutes les fonctionnalités de DiscreetLux</p>
                      <p className="text-gray-400 text-xs mb-3">Photos, messagerie, chat en direct, recherche avancée...</p>
                      <Link 
                        to="/subscription" 
                        className="inline-block px-4 py-2 bg-gold text-dark text-sm font-semibold hover:bg-gold-light transition-colors"
                      >
                        Abonnez-vous
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
